import { Injectable } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ScorecardStore, ScorecardState } from './scorecard.store';
import { Scorecard } from './models/scorecard.model';
import { Sort } from '../../shared/sort.util';
import { DateTime } from 'luxon';

@Injectable({ providedIn: 'root' })
export class ScorecardQuery extends QueryEntity<ScorecardState> {
  constructor(protected store: ScorecardStore) {
    super(store);
  }

  selectCompletedScorecardIds(): Observable<number[]> {
    return this.selectAll({ filterBy: (scorecard) => scorecard.Completed }).pipe(
      map((scorecards) => scorecards.map((s) => s.ScorecardId))
    );
  }

  hasLinkedScorecards(scorecardId: number) {
    return this.hasEntity((s: Scorecard) => s.ParentId === scorecardId);
  }


  /** sort scorecard for parent next to child,
   *  sort completed to bottom,
   *  hide scorecard linked when it completed, show 2 at a time,
   */
  selectAllScorecardSorted() {
    return this.selectAll({
      filterBy: scorecard => {
        const now = DateTime.fromISO((new Date()).toISOString(), { zone: 'utc' });
        const endDate = DateTime.fromISO(scorecard.EndDate as any, { zone: 'utc' });
        const isValid = now < endDate;
        return scorecard.Favourite == 1 && isValid
      }
    }).pipe(map(scorecards => {
      let scorecardHasLinked: Scorecard[] = [];
      let scorecardNoLinkedAndParent: Scorecard[] = [];
      let filtered: Array<any> = [];

      scorecards.forEach((scorecard) => {
        if (scorecard.ParentId) {
          scorecardHasLinked.push(scorecard); // get all child scorecard which has parentId
        } else {
          scorecardNoLinkedAndParent.push(scorecard); // get all parent scorecard (parentId=null) and no linked scorecard
        }
      });
      // parent next to child
      scorecardHasLinked.forEach((scorecard) => {
        let parent: Scorecard | undefined;
        parent = scorecardNoLinkedAndParent.find((s) => s.ScorecardId === scorecard.ParentId); /* find first parent scorecard */
        if (!parent)
          parent = scorecardHasLinked.find(
            (s) => s.ScorecardId === scorecard.ParentId
          ); /* if no parent that mean that scorecard is 3rd linked scorecard so find parent scorecard in linked */
        parent && filtered.push(parent);
        filtered.push(scorecard);
      });

      // hide completed scorecard, show only 2 scorecard at a time

      filtered = [...new Set([...filtered, ...scorecardNoLinkedAndParent])]; // remove duplicate in array
      let start: Scorecard | null;
      let end: Scorecard | null;

      filtered.forEach((s: Scorecard, idx) => {
        filtered[idx] = { ...s, Hidden: false }; // set property Hideen into each scorecard
        // get first parent
        if (!s.ParentId) {
          start = s;
        }
        // get last child of that linked
        if (start && s.ScorecardId !== start.ScorecardId && !filtered[idx + 1]?.ParentId) {
          // last condition mean next scorecard hasn't parentId, so current is the last one
          end = filtered[idx];
        }
        // already has first and last of the linked
        if (start && end) {
          const indexStart = filtered.findIndex((x) => x.ScorecardId == start?.ScorecardId);
          const indexEnd = filtered.findIndex((x) => x.ScorecardId == end?.ScorecardId);
          let family: Scorecard[] = filtered.slice(indexStart, indexEnd + 1);
          if (family.length < 3) {
            start = null;
            end = null;
            return;
          }

          let hasUncompletedScorecard = family.some((f) => f.Completed == false);
          if (hasUncompletedScorecard) {
            const uncompletedIdx = family.findIndex((x) => !x.Completed);
            family.forEach((x, idx) => {
              // hide completed scorecard in linked
              if (
                (x.Completed && !x?.PrizeOptions) ||
                x.HasCustomerSelectedPrize == true ||
                (idx !== uncompletedIdx && idx !== uncompletedIdx + 1)
              ) {
                filtered[filtered.indexOf(x)] = { ...x, Hidden: true };
              }
            });
          }
          start = null;
          end = null;
        }
      });
      return [...filtered].sort(Sort.by('Completed', 'asc'));
    }))

  }
}
